import React, { useState, useEffect, useRef, Fragment, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Transition, Combobox } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../config/firebase';
import { hasPageAccessSync, hasFeatureAccessSync } from '../utils/accessControl';
import { APP_TOP_BAR_LINKS } from '../config/permissionsRegistry';
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  getDocs,
  orderBy,
  limit,
  startAt,
  endAt,
  or,
  writeBatch,
  doc,
  getDoc
} from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useLogo } from '../contexts/LogoContext';
import PageHandler from './PageHandler';
import { useFilteredNavMenuItems, useFilteredNavGroups, isNavItemActive } from './v2/navMenuConfig';
import { getContrastTextColor } from '../utils/colorContrast';
import { getTopbarBg, getTopbarTextColor, getTopbarMutedTextColor, getTopbarControlColor } from '../theme/surfaces';
import { 
  FaSearch, 
  FaCog, 
  FaUserCircle, 
  FaSun,
  FaMoon,
  FaBars,
  FaChevronDown,
  FaUser,
  FaUserTie,
  FaBuilding,
  FaUsers,
  FaTools,
  FaPoundSign,
  FaChevronRight,
  FaShieldAlt,
  FaBell,
  FaFileDownload,
  FaFilter,
  FaCalendar,
  FaEnvelope,
  FaCertificate,
  FaCreditCard,
  FaHome,
  FaChartLine,
  FaArchive,
  FaLink,
  FaBullhorn,
  FaComments,
  FaClipboardCheck,
  FaTimes,
} from 'react-icons/fa';
import { isFeatureEnabled } from '../utils/featureFlags';
import { FEATURES } from '../features';
import '../styles/TopBarV2.css';
import GlobalSearchModal from './search/GlobalSearchModal';
import AvatarUserMenu from './topbar/AvatarUserMenu';
import {
  buildAutoSuggestionResults,
  getRecentSearches,
  addRecentSearch,
} from '../utils/searchSuggestions';

const TopBarV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const filteredNavItems = useFilteredNavMenuItems();
  const filteredNavGroups = useFilteredNavGroups();
  const [notificationCount, setNotificationCount] = useState(0);
  const { currentUser, logout, refreshUser, userRole, userPermissions } = useAuth();
  const { isDarkMode, toggleTheme, primaryColor, secondaryColor } = useTheme();
  const primaryContrastText = useMemo(() => getContrastTextColor(primaryColor), [primaryColor]);
  const { getDisplayLogo } = useLogo();
  const displayLogo = getDisplayLogo(isDarkMode);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const searchInputRef = useRef(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchModalRef = useRef(null);
  const modalRef = useRef(null);
  const searchButtonRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const [localUserRole, setLocalUserRole] = useState(null);
  
  // Helper function to check page access using permissions from AuthContext
  const checkPageAccess = useCallback((pageName) => {
    if (!userRole || !pageName) return false;
    
    // Administrators and owners always have access
    const normalizedRole = userRole.toLowerCase();
    if (normalizedRole === 'administrator' || normalizedRole === 'owner') return true;
    
    // Check user permissions from context
    if (userPermissions?.pages && pageName in userPermissions.pages) {
      return userPermissions.pages[pageName] === true;
    }
    
    return false;
  }, [userRole, userPermissions]);

  // Helper function to check feature access using permissions from AuthContext
  const checkFeatureAccess = useCallback((featureName) => {
    if (!userRole || !featureName) return false;
    
    // Administrators and owners always have access
    const normalizedRole = userRole.toLowerCase();
    if (normalizedRole === 'administrator' || normalizedRole === 'owner') return true;
    
    // Check user permissions from context
    if (userPermissions?.features && featureName in userPermissions.features) {
      return userPermissions.features[featureName] === true;
    }
    
    return false;
  }, [userRole, userPermissions]);

  // Helper to check TopBar link visibility — mirrors sidebar logic:
  // visible if the user has page access OR the required feature is enabled.
  const checkTopBarLinkAccess = useCallback((linkId) => {
    if (!userRole || !linkId) return false;
    const normalizedRole = userRole.toLowerCase();
    if (normalizedRole === 'administrator' || normalizedRole === 'owner') return true;
    const linkDef = APP_TOP_BAR_LINKS.find(l => l.id === linkId);
    if (!linkDef) return false;
    const hasPageAccess = userPermissions?.pages?.[linkDef.pageName] === true;
    const hasFeatureAccess = linkDef.requiredFeature
      ? userPermissions?.features?.[linkDef.requiredFeature] === true
      : false;
    return hasPageAccess || hasFeatureAccess;
  }, [userRole, userPermissions]);
  const [hasRefreshed, setHasRefreshed] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const [welcomeMessage, setWelcomeMessage] = useState('Nice to see you again');
  const [currentDate, setCurrentDate] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  /** Top bar search pill: same scale as NewCustomButton compact + theme colors */
  const searchPillStyle = useMemo(() => {
    const normalizeHex = (c, fallback) => {
      if (!c || typeof c !== 'string') return fallback;
      let h = c.trim().replace(/^#/, '');
      if (h.length === 3) h = h.split('').map((x) => x + x).join('');
      if (!/^[0-9A-Fa-f]{6}$/.test(h)) return fallback;
      return `#${h}`;
    };
    const pri = normalizeHex(primaryColor, '#4d8063');
    const textCol = normalizeHex(secondaryColor, isDarkMode ? '#E5E7EB' : '#1F1F1F');
    const r = parseInt(pri.slice(1, 3), 16) / 255;
    const g = parseInt(pri.slice(3, 5), 16) / 255;
    const b = parseInt(pri.slice(5, 7), 16) / 255;
    const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const iconFg = L > 0.55 ? '#1a1a1a' : '#ffffff';
    return {
      '--search-pill-bg': `${pri}27`,
      '--search-pill-bg-hover': `${pri}49`,
      '--search-pill-primary': pri,
      '--search-pill-text': textCol,
      '--search-pill-icon-fg': iconFg,
    };
  }, [primaryColor, secondaryColor, isDarkMode]);

  // Convert hex color to RGB for opacity transformations
  const hexToRgb = (hex) => {
    // Remove the # if present
    hex = hex.replace('#', '');
    
    // Parse the hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Return as RGB object
    return { r, g, b };
  };

  // Helper function to get user's first letter for avatar
  const getUserInitial = () => {
    if (!currentUser) return 'U';
    
    return (
      currentUser.displayName?.charAt(0).toUpperCase() || 
      currentUser.firstName?.charAt(0).toUpperCase() || 
      currentUser.lastName?.charAt(0).toUpperCase() || 
      currentUser.email?.charAt(0).toUpperCase() || 
      'U'
    );
  };

  // Categories for search
  const searchCategories = [
    { id: 'pages', name: 'Pages', icon: FaHome },
    { id: 'properties', name: 'Properties', icon: FaBuilding },
    { id: 'tenants', name: 'Tenants', icon: FaUsers },
    { id: 'maintenance', name: 'Maintenance', icon: FaTools },
    { id: 'finance', name: 'Finance', icon: FaPoundSign },
    { id: 'payments', name: 'Payments', icon: FaCreditCard },
    { id: 'landlords', name: 'Landlords', icon: FaUser },
    { id: 'users', name: 'Users', icon: FaUserTie },
    { id: 'links', name: 'Links', icon: FaChevronRight },
    { id: 'documents', name: 'Documents', icon: FaFileDownload },
    { id: 'certificates', name: 'Certificates', icon: FaCertificate },
    { id: 'calendar', name: 'Calendar', icon: FaCalendar },
    { id: 'messages', name: 'Messages', icon: FaEnvelope },
  ];

  // Define all available pages with their access requirements
  const allPages = [
    { title: 'Dashboard', path: '/dashboard', icon: FaHome, pageName: 'dashboard', requiredFeature: 'viewDashboard' },
    { title: 'Properties', path: '/properties', icon: FaBuilding, pageName: 'properties', requiredFeature: 'viewProperties' },
    { title: 'Tenants', path: '/tenants', icon: FaUsers, pageName: 'tenants', requiredFeature: 'viewTenants' },
    { title: 'Landlords', path: '/landlords', icon: FaUserTie, pageName: 'landlords', requiredFeature: 'viewLandlords' },
    { title: 'Compliance', path: '/compliance', icon: FaShieldAlt, pageName: 'properties', requiredFeature: 'viewProperties' },
    { title: 'Inspections', path: '/inspections', icon: FaClipboardCheck, pageName: 'inspections', requiredFeature: 'viewInspections' },
    { title: 'Maintenance', path: '/maintenance', icon: FaTools, pageName: 'maintenance', requiredFeature: 'viewMaintenance' },
    { title: 'Calendar', path: '/calendar', icon: FaCalendar, pageName: 'calendar', requiredFeature: 'viewCalendar' },
    { title: 'Finance', path: '/finance', icon: FaPoundSign, pageName: 'finance', requiredFeature: 'viewFinance' },
    { title: 'Reports', path: '/reports', icon: FaChartLine, pageName: 'reports', requiredFeature: 'viewReports' },
    { title: 'Links', path: '/links', icon: FaLink, pageName: 'links', requiredFeature: 'viewLinks' },
    { title: 'Company Docs', path: '/company-documents', icon: FaFileDownload, pageName: 'companyDocuments', requiredFeature: 'viewDocuments' },
    { title: 'Broadcast', path: '/broadcast', icon: FaBullhorn, pageName: 'broadcast', requiredFeature: 'sendBroadcasts' },
    { title: 'Messages', path: '/messages', icon: FaComments, pageName: 'messages', requiredFeature: 'sendMessages' },
    { title: 'Contractors', path: '/contractors', icon: FaTools, pageName: 'contractors', requiredFeature: 'viewContractors' },
    { title: 'Contractor messages', path: '/contractor-messages', icon: FaComments, pageName: 'contractors', requiredFeature: 'viewContractors' },
    { title: 'Archive', path: '/archive', icon: FaArchive, pageName: 'archive', requiredFeature: 'viewArchive', adminOnly: true },
    { title: 'Settings', path: '/settings', icon: FaCog, pageName: 'settings', requiredFeature: 'accessSettings' },
    { title: 'Profile', path: '/profile', icon: FaUserCircle, pageName: 'profile', requiredFeature: null },
    { title: 'Account Security', path: '/account-management', icon: FaShieldAlt, pageName: 'account', requiredFeature: null },
    { title: 'Notifications', path: '/notifications', icon: FaBell, pageName: 'notifications', requiredFeature: null },
  ];

  // Settings sections
  const settingsSections = [
    { title: 'Users', path: '/settings?tab=users', icon: FaUsers, pageName: 'users', requiredFeature: 'viewUsers' },
    { title: 'Roles & Permissions', path: '/settings?tab=roles', icon: FaShieldAlt, pageName: 'roles', requiredFeature: 'manageRoles' },
    { title: 'Branding', path: '/settings?tab=branding', icon: FaCog, pageName: 'branding', requiredFeature: 'manageBranding' },
    { title: 'Audit Logs', path: '/settings?tab=audit', icon: FaFileDownload, pageName: 'audit', requiredFeature: 'viewAuditLogs' },
    { title: 'System Configuration', path: '/settings?tab=system', icon: FaCog, pageName: 'system', requiredFeature: 'systemConfig' },
  ];

  useEffect(() => {
    if (!currentUser) return;

    const notificationsRef = collection(db, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      where('userId', '==', currentUser.uid),
      where('closed', '==', false),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      setNotificationCount(snapshot.size);
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    // Listen for both system notifications and messages
    const conversationsRef = collection(db, 'conversations');
    const conversationsQuery = query(
      conversationsRef,
      where('participants', 'array-contains', currentUser.uid)
    );

    // Create notification for unread messages
    const handleUnreadMessages = async (conversations) => {
      const batch = writeBatch(db);
      let newNotificationsCount = 0;

      for (const convDoc of conversations.docs) {
        const conv = convDoc.data();
        if (conv.lastMessage && 
            conv.lastMessageSender !== currentUser.uid && 
            (!conv.lastRead?.[currentUser.uid] || 
             conv.lastMessageTime.seconds > conv.lastRead[currentUser.uid].seconds)) {
          
          // Check if notification already exists (not closed)
          // We check for notifications that are not closed, regardless of read status
          // This prevents recreating notifications that were marked as read
          const existingQuery = query(
            collection(db, 'notifications'),
            where('type', '==', 'NEW_MESSAGE'),
            where('conversationId', '==', convDoc.id),
            where('userId', '==', currentUser.uid),
            where('closed', '==', false)
          );
          const existing = await getDocs(existingQuery);
          
          if (existing.empty) {
            newNotificationsCount++;
            const notificationRef = doc(collection(db, 'notifications'));
            batch.set(notificationRef, {
              type: 'NEW_MESSAGE',
              userId: currentUser.uid,
              conversationId: convDoc.id,
              message: conv.lastMessage,
              timestamp: conv.lastMessageTime,
              read: false,
              closed: false,
              sender: conv.participantDetails?.[conv.lastMessageSender]?.name || 'Unknown',
              subject: conv.propertyAddress || 'General Discussion'
            });
          }
        }
      }

      if (newNotificationsCount > 0) {
        await batch.commit();
      }
    };

    const unsubscribeMessages = onSnapshot(conversationsQuery, (snapshot) => {
      handleUnreadMessages(snapshot).catch(console.error);
    });

    return () => unsubscribeMessages();
  }, [currentUser]);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        if (currentUser) {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setLocalUserRole(userData.role);
          }
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };

    fetchUserRole();
  }, [currentUser]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current && 
        searchButtonRef.current && 
        !modalRef.current.contains(event.target) && 
        !searchButtonRef.current.contains(event.target)
      ) {
        setShowSearchModal(false);
      }
    };

    if (showSearchModal) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSearchModal]);

  // Modify the useEffect hook to prevent an infinite loop
  useEffect(() => {
    // Refresh user data to ensure we have the latest displayName
    const updateUserData = async () => {
      try {
        const now = Date.now();
        // Only refresh if it's been more than 5 minutes since the last refresh
        // or if we haven't refreshed yet
        if (currentUser && (!hasRefreshed || now - lastRefreshTime > 300000)) {
          setHasRefreshed(true);
          setLastRefreshTime(now);
          const updatedUser = await refreshUser();
          // Only log in development environment
          if (process.env.NODE_ENV !== 'production') {
            console.log("User data refreshed in TopBar:", updatedUser?.displayName || currentUser?.displayName);
          }
        }
      } catch (error) {
        console.error("Error refreshing user data in TopBar:", error);
      }
    };
    
    updateUserData();
    // Only run this effect when currentUser changes or refreshUser function changes
  }, [currentUser?.uid, refreshUser, hasRefreshed, lastRefreshTime]);

  // Add useEffect to set a random welcome message and current date
  useEffect(() => {
    const welcomeMessages = [
      'Nice to see you again',
      'Welcome back',
      'Good to have you back',
      'Hello there',
      'Great to see you',
      'Welcome'
    ];
    
    const randomMessage = welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];
    setWelcomeMessage(randomMessage);

    // Format current date
    const now = new Date();
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    const formattedDate = now.toLocaleDateString('en-US', options);
    setCurrentDate(formattedDate);
  }, []);

  // Add useEffect to handle responsive behavior for hamburger menu and search icon
  useEffect(() => {
    const checkScreenSize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };

    // Check on mount
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleNotificationsClick = () => {
    navigate('/notifications');
  };

  // Filter search results based on selected categories
  const filterResultsByCategory = (results) => {
    if (selectedCategories.length === 0) return results;
    return results.filter(result => selectedCategories.includes(result.category));
  };

  // Toggle category filter
  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  // Get available categories based on user role
  const getAvailableCategories = () => {
    const baseCategories = [
      { id: 'pages', name: 'Pages', icon: FaHome },
      { id: 'properties', name: 'Properties', icon: FaBuilding },
      { id: 'maintenance', name: 'Maintenance', icon: FaTools },
      { id: 'links', name: 'Links', icon: FaChevronRight },
    ];

    if (['agent', 'owner', 'administrator'].includes(userRole)) {
      baseCategories.push(
        { id: 'tenants', name: 'Tenants', icon: FaUsers },
        { id: 'landlords', name: 'Landlords', icon: FaUser },
        { id: 'documents', name: 'Documents', icon: FaFileDownload },
        { id: 'certificates', name: 'Certificates', icon: FaCertificate },
        { id: 'calendar', name: 'Calendar', icon: FaCalendar }
      );
    }

    if (['owner', 'administrator'].includes(userRole)) {
      baseCategories.push({ id: 'users', name: 'Users', icon: FaUserTie });
      if (isFeatureEnabled(FEATURES.FINANCE_MODULE)) {
        baseCategories.push(
          { id: 'finance', name: 'Finance', icon: FaPoundSign },
          { id: 'payments', name: 'Payments', icon: FaCreditCard }
        );
      }
    }

    return baseCategories;
  };

  const accessibleSearchPages = useMemo(() => {
    const combined = [...allPages, ...settingsSections];
    return combined.filter((page) => {
      if (!isFeatureEnabled(FEATURES.FINANCE_MODULE) && (page.path === '/finance' || page.path === '/reports')) {
        return false;
      }
      if (page.path === '/messages') return false;
      if (page.adminOnly) {
        const normalizedRole = userRole?.toLowerCase();
        const isAdmin = normalizedRole === 'administrator';
        const isOwner = normalizedRole === 'owner';
        if (!isAdmin && !isOwner) return false;
      }
      if (page.pageName && userRole) {
        if (!checkPageAccess(page.pageName)) return false;
      }
      if (page.requiredFeature && userRole) {
        if (!checkFeatureAccess(page.requiredFeature)) return false;
      }
      return true;
    });
  }, [userRole, userPermissions, checkPageAccess, checkFeatureAccess]);

  // Auto suggestions for empty or short queries (pages + recent searches)
  useEffect(() => {
    if (searchQuery.trim().length >= 2) return;

    const suggestionGroups = buildAutoSuggestionResults({
      query: searchQuery,
      accessiblePages: accessibleSearchPages,
      recentSearches: getRecentSearches(),
    }).map((group) => ({
      ...group,
      icon: group.category === 'recent' ? FaSearch : FaHome,
    }));

    setSearchResults(filterResultsByCategory(suggestionGroups));
    setIsSearching(false);
  }, [searchQuery, selectedCategories, accessibleSearchPages]);

  // Perform universal search across multiple collections
  const performSearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      return;
    }

    searchRequestIdRef.current += 1;
    const thisRequestId = searchRequestIdRef.current;
    setSearchResults([]);
    setIsSearching(true);
    const searchResults = [];
    const lowerSearchTerm = searchTerm.toLowerCase();

    const isStale = () => thisRequestId !== searchRequestIdRef.current;

    try {
      // Search Pages - Filtered by user permissions
      const accessiblePages = allPages.filter(page => {
        if (!isFeatureEnabled(FEATURES.FINANCE_MODULE) && (page.path === '/finance' || page.path === '/reports')) {
          return false;
        }
        if (page.path === '/messages') return false;
        if (page.adminOnly) {
          const normalizedRole = userRole?.toLowerCase();
          const isAdmin = normalizedRole === 'administrator';
          const isOwner = normalizedRole === 'owner';
          if (!isAdmin && !isOwner) {
            return false;
          }
        }
        if (page.pageName && userRole) {
          const hasPage = checkPageAccess(page.pageName);
          if (!hasPage) return false;
        }
        if (page.requiredFeature && userRole) {
          const hasFeature = checkFeatureAccess(page.requiredFeature);
          if (!hasFeature) return false;
        }
        return true;
      });

      const pageMatches = accessiblePages.filter(page => {
        const matches = (
          page.title?.toLowerCase().includes(lowerSearchTerm) ||
          page.path?.toLowerCase().includes(lowerSearchTerm) ||
          page.pageName?.toLowerCase().includes(lowerSearchTerm)
        );
        return matches;
      }).slice(0, 5);

      if (pageMatches.length > 0) {
        const pageResults = {
          category: 'pages',
          icon: FaHome,
          items: pageMatches.map(page => ({
            id: page.path,
            title: page.title,
            path: page.path,
          })),
        };
        searchResults.push(pageResults);
      }

      // Search Settings Sections - Filtered by user permissions
      if (userRole && checkPageAccess('settings')) {
        const accessibleSettings = settingsSections.filter(section => {
          if (section.requiredFeature && userRole) {
            return hasFeatureAccessSync(userRole, section.requiredFeature);
          }
          return true;
        });

        const settingsMatches = accessibleSettings.filter(section => {
          const matches = (
            section.title?.toLowerCase().includes(lowerSearchTerm) ||
            section.path?.toLowerCase().includes(lowerSearchTerm) ||
            section.pageName?.toLowerCase().includes(lowerSearchTerm)
          );
          return matches;
        }).slice(0, 5);

        if (settingsMatches.length > 0) {
          const settingsResults = {
            category: 'pages',
            icon: FaCog,
            items: settingsMatches.map(section => ({
              id: section.path,
              title: section.title,
              subtitle: 'Settings section',
              path: section.path,
            })),
          };
          searchResults.push(settingsResults);
        }
      }

      // Search Properties
      const propertiesRef = collection(db, 'properties');
      let propertiesQuery = propertiesRef;
      if (userRole === 'landlord' && currentUser) {
        propertiesQuery = query(propertiesRef, where('landlordId', '==', currentUser.uid));
      }

      const linksRef = collection(db, 'links');

      const fetchOpt = (p) => p.catch((e) => { console.warn(e?.message || e); return { docs: [] }; });
      const [propertiesSnapshot, linksSnapshot] = await Promise.all([
        fetchOpt(getDocs(propertiesQuery)),
        fetchOpt(getDocs(linksRef)),
      ]);

      const propertyMatches = propertiesSnapshot.docs.filter(doc => {
        const data = doc.data();
        const searchTerms = data.searchTerms || [];
        const matches = (
          data.address?.toLowerCase().includes(lowerSearchTerm) ||
          data.propertyType?.toLowerCase().includes(lowerSearchTerm) ||
          data.propertyName?.toLowerCase().includes(lowerSearchTerm) ||
          searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
        );
        return matches;
      }).slice(0, 5);

      if (propertyMatches.length > 0) {
        const propertyResults = {
          category: 'properties',
          icon: FaBuilding,
          items: propertyMatches.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              title: data.address || data.propertyName || 'Unnamed Property',
              subtitle: `${data.bedrooms || 'N/A'} bed ${data.propertyType || 'Property'}`,
            };
          }),
        };
        searchResults.push(propertyResults);
      }

      const propertyAddressById = new Map(
        propertiesSnapshot.docs.map((d) => {
          const data = d.data();
          return [d.id, data.address || data.propertyName || null];
        })
      );

      // Search Tenants - Only for agents, owners, and administrators
      let tenantsSnapshot = { docs: [] };
      let landlordsSnapshot = { docs: [] };
      if (['agent', 'owner', 'administrator'].includes(userRole)) {
        const tenantsRef = collection(db, 'tenants');
        let tenantsQuery = tenantsRef;
        if (userRole === 'agent' && currentUser) {
          tenantsQuery = query(tenantsRef, where('agentId', '==', currentUser.uid));
        }
        const landlordsQuery = query(collection(db, 'users'), where('role', '==', 'landlord'));
        const fetchOpt2 = (p) => p.catch((e) => { console.warn(e?.message || e); return { docs: [] }; });
        const [tSnap, lSnap] = await Promise.all([
          fetchOpt2(getDocs(tenantsQuery)),
          fetchOpt2(getDocs(landlordsQuery)),
        ]);
        tenantsSnapshot = tSnap;
        landlordsSnapshot = lSnap;
      }

      if (['agent', 'owner', 'administrator'].includes(userRole)) {
        const tenantMatches = tenantsSnapshot.docs.filter(doc => {
          const data = doc.data();
          const searchTerms = data.searchTerms || [];
          const matches = (
            data.firstName?.toLowerCase().includes(lowerSearchTerm) ||
            data.lastName?.toLowerCase().includes(lowerSearchTerm) ||
            data.email?.toLowerCase().includes(lowerSearchTerm) ||
            `${data.firstName} ${data.lastName}`.toLowerCase().includes(lowerSearchTerm) ||
            searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
          );
          return matches;
        }).slice(0, 5);

        if (tenantMatches.length > 0) {
          const tenantResults = {
            category: 'tenants',
            icon: FaUsers,
            items: tenantMatches.map(doc => {
              const data = doc.data();
              const address = data.propertyAddress || (data.propertyId ? propertyAddressById.get(data.propertyId) : null);
              return {
                id: doc.id,
                title: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unnamed Tenant',
                subtitle: address || 'No property assigned',
              };
            }),
          };
          searchResults.push(tenantResults);
        }
      }

      // Search Maintenance Requests
      const maintenanceRef = collection(db, 'maintenanceRequests');
      const landlordPropertyIds = (userRole === 'landlord' && propertiesSnapshot?.docs) ? propertiesSnapshot.docs.map(doc => doc.id) : [];

      if (userRole === 'landlord' && currentUser && landlordPropertyIds.length > 0) {
          const batchSize = 10;
          let allMaintenanceDocs = [];
          for (let i = 0; i < landlordPropertyIds.length; i += batchSize) {
            const batchIds = landlordPropertyIds.slice(i, i + batchSize);
            try {
              const batchQuery = query(maintenanceRef, where('propertyId', 'in', batchIds));
              const batchSnapshot = await getDocs(batchQuery);
              allMaintenanceDocs = [...allMaintenanceDocs, ...batchSnapshot.docs];
            } catch (permissionError) {
              console.warn('Maintenance batch query permission denied, skipping...', permissionError);
            }
          }
          
          const maintenanceMatches = allMaintenanceDocs.filter(doc => {
            const data = doc.data();
            const searchTerms = data.searchTerms || [];
            const matches = (
              data.title?.toLowerCase().includes(lowerSearchTerm) ||
              data.description?.toLowerCase().includes(lowerSearchTerm) ||
              data.status?.toLowerCase().includes(lowerSearchTerm) ||
              data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
              searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
            );
            return matches;
          }).slice(0, 5);

          if (maintenanceMatches.length > 0) {
            const maintenanceResults = {
              category: 'maintenance',
              icon: FaTools,
              items: maintenanceMatches.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  title: data.title || 'Untitled Request',
                  subtitle: `${data.status || 'No status'} - ${data.propertyAddress || 'Unknown Property'}`,
                };
              }),
            };
            searchResults.push(maintenanceResults);
          }
      } else if (userRole === 'agent' && currentUser) {
        // For agents, get properties they manage first
        let agentPropertiesSnapshot;
        try {
          agentPropertiesSnapshot = await getDocs(
            query(collection(db, 'properties'), where('agentId', '==', currentUser.uid))
          );
        } catch (permissionError) {
          console.warn('Agent properties query permission denied, skipping...', permissionError);
          agentPropertiesSnapshot = { docs: [] };
        }
        
        const propertyIds = agentPropertiesSnapshot.docs.map(doc => doc.id);
        
        if (propertyIds.length > 0) {
          const batchSize = 10;
          let allMaintenanceDocs = [];
          
          for (let i = 0; i < propertyIds.length; i += batchSize) {
            const batchIds = propertyIds.slice(i, i + batchSize);
            try {
              const batchQuery = query(maintenanceRef, where('propertyId', 'in', batchIds));
              const batchSnapshot = await getDocs(batchQuery);
              allMaintenanceDocs = [...allMaintenanceDocs, ...batchSnapshot.docs];
            } catch (permissionError) {
              console.warn('Maintenance batch query permission denied, skipping...', permissionError);
            }
          }
          
          const maintenanceMatches = allMaintenanceDocs.filter(doc => {
            const data = doc.data();
            const searchTerms = data.searchTerms || [];
            const matches = (
              data.title?.toLowerCase().includes(lowerSearchTerm) ||
              data.description?.toLowerCase().includes(lowerSearchTerm) ||
              data.status?.toLowerCase().includes(lowerSearchTerm) ||
              data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
              searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
            );
            return matches;
          }).slice(0, 5);

          if (maintenanceMatches.length > 0) {
            const maintenanceResults = {
              category: 'maintenance',
              icon: FaTools,
              items: maintenanceMatches.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  title: data.title || 'Untitled Request',
                  subtitle: `${data.status || 'No status'} - ${data.propertyAddress || 'Unknown Property'}`,
                };
              }),
            };
            searchResults.push(maintenanceResults);
          }
        }
      } else if (['owner', 'administrator'].includes(userRole)) {
        // For owners and administrators, search all maintenance requests
        let maintenanceSnapshot;
        try {
          maintenanceSnapshot = await getDocs(maintenanceRef);
        } catch (permissionError) {
          console.warn('Maintenance search permission denied, skipping...', permissionError);
          maintenanceSnapshot = { docs: [] };
        }
        
        const maintenanceMatches = maintenanceSnapshot.docs.filter(doc => {
          const data = doc.data();
          const searchTerms = data.searchTerms || [];
          const matches = (
            data.title?.toLowerCase().includes(lowerSearchTerm) ||
            data.description?.toLowerCase().includes(lowerSearchTerm) ||
            data.status?.toLowerCase().includes(lowerSearchTerm) ||
            data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
            searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
          );
          return matches;
        }).slice(0, 5);

        if (maintenanceMatches.length > 0) {
          const maintenanceResults = {
            category: 'maintenance',
            icon: FaTools,
            items: maintenanceMatches.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                title: data.title || 'Untitled Request',
                subtitle: `${data.status || 'No status'} - ${data.propertyAddress || 'Unknown Property'}`,
              };
            }),
          };
          searchResults.push(maintenanceResults);
        }
      }

      // Search Landlords - use pre-fetched landlordsSnapshot from parallel wave
      if (['agent', 'owner', 'administrator'].includes(userRole)) {
        const landlordMatches = landlordsSnapshot.docs.filter(doc => {
          const data = doc.data();
          const searchTerms = data.searchTerms || [];
          const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim().toLowerCase();
          
          const matches = (
            data.firstName?.toLowerCase().includes(lowerSearchTerm) ||
            data.lastName?.toLowerCase().includes(lowerSearchTerm) ||
            data.email?.toLowerCase().includes(lowerSearchTerm) ||
            fullName.includes(lowerSearchTerm) ||
            data.phone?.toLowerCase().includes(lowerSearchTerm) ||
            data.company?.toLowerCase().includes(lowerSearchTerm) ||
            searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
          );
          return matches;
        }).slice(0, 5);

        if (landlordMatches.length > 0) {
          const landlordResults = {
            category: 'landlords',
            icon: FaUserTie,
            items: landlordMatches.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                title: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unnamed Landlord',
                subtitle: data.email || data.phone || 'No contact info',
              };
            }),
          };
          searchResults.push(landlordResults);
        }
      }

      // Search Finance Records - Only when Finance module enabled, for owners and administrators
      if (isFeatureEnabled(FEATURES.FINANCE_MODULE) && ['owner', 'administrator'].includes(userRole)) {
        const financeRef = collection(db, 'finances');
        let financeQuery = financeRef;
        
        // For owners, only show their own finances
        if (userRole === 'owner' && currentUser) {
          financeQuery = query(financeRef, where('ownerId', '==', currentUser.uid));
        }
        
        let financeSnapshot;
        try {
          financeSnapshot = await getDocs(financeQuery);
        } catch (permissionError) {
          console.warn('Finance search permission denied, skipping...', permissionError);
          financeSnapshot = { docs: [] };
        }
        
        const financeMatches = financeSnapshot.docs.filter(doc => {
          const data = doc.data();
          const searchTerms = data.searchTerms || [];
          const matches = (
            data.type?.toLowerCase().includes(lowerSearchTerm) ||
            data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
            data.description?.toLowerCase().includes(lowerSearchTerm) ||
            searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
          );
          return matches;
        }).slice(0, 5);

        if (financeMatches.length > 0) {
          const financeResults = {
            category: 'finance',
            icon: FaPoundSign,
            items: financeMatches.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                title: `${data.type} - £${data.amount}`,
                subtitle: `${data.propertyAddress} - ${new Date(data.date).toLocaleDateString()}`,
              };
            }),
          };
          searchResults.push(financeResults);
        }
      }

      // Search Links - use pre-fetched linksSnapshot from parallel wave
      const linkMatches = linksSnapshot.docs.filter(doc => {
        const data = doc.data();
        const searchTerms = data.searchTerms || [];
        const matches = (
          data.title?.toLowerCase().includes(lowerSearchTerm) ||
          data.description?.toLowerCase().includes(lowerSearchTerm) ||
          data.category?.toLowerCase().includes(lowerSearchTerm) ||
          data.url?.toLowerCase().includes(lowerSearchTerm) ||
          searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
        );
        return matches;
      }).slice(0, 5);

      if (linkMatches.length > 0) {
        const linkResults = {
          category: 'links',
          icon: FaChevronRight,
          items: linkMatches.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              title: data.title || 'Untitled Link',
              subtitle: data.category || data.description || 'No description',
            };
          }),
        };
        searchResults.push(linkResults);
      }

      // Search Documents - Role-based filtering
      const documentsRef = collection(db, 'documents');
      let documentsQuery = documentsRef;
      
      if (userRole === 'landlord' && currentUser) {
        // For landlords, get their property IDs first
        let landlordPropertiesSnapshot;
        try {
          landlordPropertiesSnapshot = await getDocs(
            query(collection(db, 'properties'), where('landlordId', '==', currentUser.uid))
          );
        } catch (permissionError) {
          console.warn('Landlord properties query permission denied, skipping...', permissionError);
          landlordPropertiesSnapshot = { docs: [] };
        }
        
        const propertyIds = landlordPropertiesSnapshot.docs.map(doc => doc.id);
        
        if (propertyIds.length > 0) {
          // Search documents for landlord's properties only
          let documentsSnapshot;
          try {
            documentsSnapshot = await getDocs(documentsRef);
          } catch (permissionError) {
            console.warn('Documents search permission denied, skipping...', permissionError);
            documentsSnapshot = { docs: [] };
          }
          
          const documentMatches = documentsSnapshot.docs.filter(doc => {
            const data = doc.data();
            const searchTerms = data.searchTerms || [];
            const isOwnedProperty = propertyIds.includes(data.propertyId);
            const matches = isOwnedProperty && (
              data.name?.toLowerCase().includes(lowerSearchTerm) ||
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.category?.toLowerCase().includes(lowerSearchTerm) ||
              data.description?.toLowerCase().includes(lowerSearchTerm) ||
              data.fileName?.toLowerCase().includes(lowerSearchTerm) ||
              searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
            );
            return matches;
          }).slice(0, 5);

          if (documentMatches.length > 0) {
            const documentResults = {
              category: 'documents',
              icon: FaFileDownload,
              items: documentMatches.map(doc => {
                const data = doc.data();
                const address = data.propertyAddress || (data.propertyId ? propertyAddressById.get(data.propertyId) : null);
                return {
                  id: doc.id,
                  propertyId: data.propertyId,
                  title: data.name || data.fileName || 'Untitled Document',
                  subtitle: `${data.type || 'Document'} - ${address || 'Unknown Property'}`,
                };
              }),
            };
            searchResults.push(documentResults);
          }
        }
      } else {
        // For other roles, search all documents
        let documentsSnapshot;
        try {
          documentsSnapshot = await getDocs(documentsRef);
        } catch (permissionError) {
          console.warn('Documents search permission denied, skipping...', permissionError);
          documentsSnapshot = { docs: [] };
        }
        
        const documentMatches = documentsSnapshot.docs.filter(doc => {
          const data = doc.data();
          const searchTerms = data.searchTerms || [];
          const matches = (
            data.name?.toLowerCase().includes(lowerSearchTerm) ||
            data.type?.toLowerCase().includes(lowerSearchTerm) ||
            data.category?.toLowerCase().includes(lowerSearchTerm) ||
            data.description?.toLowerCase().includes(lowerSearchTerm) ||
            data.fileName?.toLowerCase().includes(lowerSearchTerm) ||
            searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
          );
          return matches;
        }).slice(0, 5);

        if (documentMatches.length > 0) {
          const documentResults = {
            category: 'documents',
            icon: FaFileDownload,
            items: documentMatches.map(doc => {
              const data = doc.data();
              const address = data.propertyAddress || (data.propertyId ? propertyAddressById.get(data.propertyId) : null);
              return {
                id: doc.id,
                propertyId: data.propertyId,
                title: data.name || data.fileName || 'Untitled Document',
                subtitle: `${data.type || 'Document'} - ${address || 'Unknown Property'}`,
              };
            }),
          };
          searchResults.push(documentResults);
        }
      }

      // Search Users - Only for owners and administrators
      if (['owner', 'administrator'].includes(userRole)) {
        const usersRef = collection(db, 'users');
        let usersSnapshot;
        try {
          usersSnapshot = await getDocs(usersRef);
        } catch (permissionError) {
          console.warn('Users search permission denied, skipping...', permissionError);
          usersSnapshot = { docs: [] };
        }
        
        const userMatches = usersSnapshot.docs.filter(doc => {
          const data = doc.data();
          // Exclude landlords - they have their own dedicated search category
          if (data.role === 'landlord') return false;
          const searchTerms = data.searchTerms || [];
          const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim().toLowerCase();
          
          const matches = (
            data.firstName?.toLowerCase().includes(lowerSearchTerm) ||
            data.lastName?.toLowerCase().includes(lowerSearchTerm) ||
            data.email?.toLowerCase().includes(lowerSearchTerm) ||
            fullName.includes(lowerSearchTerm) ||
            data.phone?.toLowerCase().includes(lowerSearchTerm) ||
            data.role?.toLowerCase().includes(lowerSearchTerm) ||
            data.company?.toLowerCase().includes(lowerSearchTerm) ||
            searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
          );
          return matches;
        }).slice(0, 5);

        if (userMatches.length > 0) {
          const userResults = {
            category: 'users',
            icon: FaUserTie,
            items: userMatches.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                title: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email || 'Unnamed User',
                subtitle: `${data.role || 'No role'} - ${data.email || 'No email'}`,
              };
            }),
          };
          searchResults.push(userResults);
        }
      }

      // Search Payments - Only when Finance module enabled, for owners and administrators
      if (isFeatureEnabled(FEATURES.FINANCE_MODULE) && ['owner', 'administrator'].includes(userRole)) {
        const paymentsRef = collection(db, 'payments');
        let paymentsQuery = paymentsRef;
        
        // For owners, filter by their properties
        if (userRole === 'owner' && currentUser) {
          // Get owner's properties first
          let ownerPropertiesSnapshot;
          try {
            ownerPropertiesSnapshot = await getDocs(
              query(collection(db, 'properties'), where('landlordId', '==', currentUser.uid))
            );
          } catch (permissionError) {
            console.warn('Owner properties query permission denied, skipping...', permissionError);
            ownerPropertiesSnapshot = { docs: [] };
          }
          
          const propertyIds = ownerPropertiesSnapshot.docs.map(doc => doc.id);
          
          if (propertyIds.length > 0) {
            // Fetch payments and filter by property IDs
            let paymentsSnapshot;
            try {
              paymentsSnapshot = await getDocs(paymentsRef);
            } catch (permissionError) {
              console.warn('Payments search permission denied, skipping...', permissionError);
              paymentsSnapshot = { docs: [] };
            }
            
            const paymentMatches = paymentsSnapshot.docs.filter(doc => {
              const data = doc.data();
              const searchTerms = data.searchTerms || [];
              const isOwnedProperty = propertyIds.includes(data.propertyId);
              const matches = isOwnedProperty && (
                data.tenantName?.toLowerCase().includes(lowerSearchTerm) ||
                data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
                data.type?.toLowerCase().includes(lowerSearchTerm) ||
                data.status?.toLowerCase().includes(lowerSearchTerm) ||
                data.amount?.toString().includes(lowerSearchTerm) ||
                searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
              );
              return matches;
            }).slice(0, 5);

            if (paymentMatches.length > 0) {
              const paymentResults = {
                category: 'payments',
                icon: FaCreditCard,
                items: paymentMatches.map(doc => {
                  const data = doc.data();
                  const dueDate = data.dueDate?.toDate ? data.dueDate.toDate() : (data.dueDate ? new Date(data.dueDate) : null);
                  return {
                    id: doc.id,
                    title: `£${data.amount || '0'} - ${data.type || 'Payment'}`,
                    subtitle: `${data.propertyAddress || 'Unknown Property'} - ${dueDate ? dueDate.toLocaleDateString() : 'No date'}`,
                  };
                }),
              };
              searchResults.push(paymentResults);
            }
          }
        } else {
          // Administrators can see all payments
          let paymentsSnapshot;
          try {
            paymentsSnapshot = await getDocs(paymentsQuery);
          } catch (permissionError) {
            console.warn('Payments search permission denied, skipping...', permissionError);
            paymentsSnapshot = { docs: [] };
          }
          
          const paymentMatches = paymentsSnapshot.docs.filter(doc => {
            const data = doc.data();
            const searchTerms = data.searchTerms || [];
            const matches = (
              data.tenantName?.toLowerCase().includes(lowerSearchTerm) ||
              data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.status?.toLowerCase().includes(lowerSearchTerm) ||
              data.amount?.toString().includes(lowerSearchTerm) ||
              searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
            );
            return matches;
          }).slice(0, 5);

          if (paymentMatches.length > 0) {
            const paymentResults = {
              category: 'payments',
              icon: FaCreditCard,
              items: paymentMatches.map(doc => {
                const data = doc.data();
                const dueDate = data.dueDate?.toDate ? data.dueDate.toDate() : (data.dueDate ? new Date(data.dueDate) : null);
                return {
                  id: doc.id,
                  title: `£${data.amount || '0'} - ${data.type || 'Payment'}`,
                  subtitle: `${data.propertyAddress || 'Unknown Property'} - ${dueDate ? dueDate.toLocaleDateString() : 'No date'}`,
                };
              }),
            };
            searchResults.push(paymentResults);
          }
        }
      }

      // Search Certificates - Role-based filtering
      if (['agent', 'owner', 'administrator', 'landlord'].includes(userRole)) {
        const certificatesRef = collection(db, 'certificates');
        let certificatesQuery = certificatesRef;
        
        // For landlords, filter by their properties
        if (userRole === 'landlord' && currentUser) {
          let landlordPropertiesSnapshot;
          try {
            landlordPropertiesSnapshot = await getDocs(
              query(collection(db, 'properties'), where('landlordId', '==', currentUser.uid))
            );
          } catch (permissionError) {
            console.warn('Landlord properties query permission denied, skipping...', permissionError);
            landlordPropertiesSnapshot = { docs: [] };
          }
          
          const propertyIds = landlordPropertiesSnapshot.docs.map(doc => doc.id);
          
          if (propertyIds.length > 0) {
            let certificatesSnapshot;
            try {
              certificatesSnapshot = await getDocs(certificatesRef);
            } catch (permissionError) {
              console.warn('Certificates search permission denied, skipping...', permissionError);
              certificatesSnapshot = { docs: [] };
            }
            
            const certificateMatches = certificatesSnapshot.docs.filter(doc => {
              const data = doc.data();
              const searchTerms = data.searchTerms || [];
              const isOwnedProperty = propertyIds.includes(data.propertyId);
              const matches = isOwnedProperty && (
                data.type?.toLowerCase().includes(lowerSearchTerm) ||
                data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
                data.status?.toLowerCase().includes(lowerSearchTerm) ||
                data.certificateNumber?.toLowerCase().includes(lowerSearchTerm) ||
                searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
              );
              return matches;
            }).slice(0, 5);

            if (certificateMatches.length > 0) {
              const certificateResults = {
                category: 'certificates',
                icon: FaCertificate,
                items: certificateMatches.map(doc => {
                  const data = doc.data();
                  const expiryDate = data.expiryDate?.toDate ? data.expiryDate.toDate() : (data.expiryDate ? new Date(data.expiryDate) : null);
                  return {
                    id: doc.id,
                    title: `${data.type || 'Certificate'} - ${data.propertyAddress || 'Unknown Property'}`,
                    subtitle: `Expires: ${expiryDate ? expiryDate.toLocaleDateString() : 'No expiry date'}`,
                  };
                }),
              };
              searchResults.push(certificateResults);
            }
          }
        } else {
          // For agents/owners/admins, search all certificates
          let certificatesSnapshot;
          try {
            certificatesSnapshot = await getDocs(certificatesQuery);
          } catch (permissionError) {
            console.warn('Certificates search permission denied, skipping...', permissionError);
            certificatesSnapshot = { docs: [] };
          }
          
          const certificateMatches = certificatesSnapshot.docs.filter(doc => {
            const data = doc.data();
            const searchTerms = data.searchTerms || [];
            const matches = (
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
              data.status?.toLowerCase().includes(lowerSearchTerm) ||
              data.certificateNumber?.toLowerCase().includes(lowerSearchTerm) ||
              searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
            );
            return matches;
          }).slice(0, 5);

          if (certificateMatches.length > 0) {
            const certificateResults = {
              category: 'certificates',
              icon: FaCertificate,
              items: certificateMatches.map(doc => {
                const data = doc.data();
                const expiryDate = data.expiryDate?.toDate ? data.expiryDate.toDate() : (data.expiryDate ? new Date(data.expiryDate) : null);
                return {
                  id: doc.id,
                  title: `${data.type || 'Certificate'} - ${data.propertyAddress || 'Unknown Property'}`,
                  subtitle: `Expires: ${expiryDate ? expiryDate.toLocaleDateString() : 'No expiry date'}`,
                };
              }),
            };
            searchResults.push(certificateResults);
          }
        }
      }

      // Search Calendar Events - Role-based filtering
      if (['agent', 'owner', 'administrator', 'landlord'].includes(userRole)) {
        const eventsRef = collection(db, 'events');
        let eventsQuery = eventsRef;
        
        // For landlords, filter by their properties
        if (userRole === 'landlord' && currentUser) {
          let landlordPropertiesSnapshot;
          try {
            landlordPropertiesSnapshot = await getDocs(
              query(collection(db, 'properties'), where('landlordId', '==', currentUser.uid))
            );
          } catch (permissionError) {
            console.warn('Landlord properties query permission denied, skipping...', permissionError);
            landlordPropertiesSnapshot = { docs: [] };
          }
          
          const propertyIds = landlordPropertiesSnapshot.docs.map(doc => doc.id);
          
          if (propertyIds.length > 0) {
            let eventsSnapshot;
            try {
              eventsSnapshot = await getDocs(eventsRef);
            } catch (permissionError) {
              console.warn('Calendar events search permission denied, skipping...', permissionError);
              eventsSnapshot = { docs: [] };
            }
            
            const eventMatches = eventsSnapshot.docs.filter(doc => {
              const data = doc.data();
              const searchTerms = data.searchTerms || [];
              const isOwnedProperty = !data.propertyId || propertyIds.includes(data.propertyId);
              const matches = isOwnedProperty && (
                data.title?.toLowerCase().includes(lowerSearchTerm) ||
                data.description?.toLowerCase().includes(lowerSearchTerm) ||
                data.type?.toLowerCase().includes(lowerSearchTerm) ||
                data.location?.toLowerCase().includes(lowerSearchTerm) ||
                searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
              );
              return matches;
            }).slice(0, 5);

            if (eventMatches.length > 0) {
              const calendarResults = {
                category: 'calendar',
                icon: FaCalendar,
                items: eventMatches.map(doc => {
                  const data = doc.data();
                  const eventDate = data.startDate?.toDate ? data.startDate.toDate() : (data.startDate ? new Date(data.startDate) : null);
                  return {
                    id: doc.id,
                    title: data.title || 'Untitled Event',
                    subtitle: `${eventDate ? eventDate.toLocaleDateString() : 'No date'} - ${data.type || 'Event'}`,
                  };
                }),
              };
              searchResults.push(calendarResults);
            }
          }
        } else {
          // For agents/owners/admins, search all events
          let eventsSnapshot;
          try {
            eventsSnapshot = await getDocs(eventsQuery);
          } catch (permissionError) {
            console.warn('Calendar events search permission denied, skipping...', permissionError);
            eventsSnapshot = { docs: [] };
          }
          
          const eventMatches = eventsSnapshot.docs.filter(doc => {
            const data = doc.data();
            const searchTerms = data.searchTerms || [];
            const matches = (
              data.title?.toLowerCase().includes(lowerSearchTerm) ||
              data.description?.toLowerCase().includes(lowerSearchTerm) ||
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.location?.toLowerCase().includes(lowerSearchTerm) ||
              searchTerms.some(term => term.toLowerCase().includes(lowerSearchTerm))
            );
            return matches;
          }).slice(0, 5);

          if (eventMatches.length > 0) {
            const calendarResults = {
              category: 'calendar',
              icon: FaCalendar,
              items: eventMatches.map(doc => {
                const data = doc.data();
                const eventDate = data.startDate?.toDate ? data.startDate.toDate() : (data.startDate ? new Date(data.startDate) : null);
                return {
                  id: doc.id,
                  title: data.title || 'Untitled Event',
                  subtitle: `${eventDate ? eventDate.toLocaleDateString() : 'No date'} - ${data.type || 'Event'}`,
                };
              }),
            };
            searchResults.push(calendarResults);
          }
        }
      }

      // Messages/conversations are excluded from search (not shown)
      
      // Apply category filters
      if (isStale()) return;
      const filteredResults = filterResultsByCategory(searchResults);
      setSearchResults(filteredResults);
    } catch (error) {
      if (isStale()) return;
      console.error('Search error:', error);
      setSearchResults([{
        category: 'error',
        items: [{
          id: 'error',
          title: 'Error performing search',
          subtitle: 'Please try again later'
        }]
      }]);
    } finally {
      if (!isStale()) setIsSearching(false);
    }
  };

  // Memoize performSearch to prevent recreating on every render
  const memoizedPerformSearch = useCallback(performSearch, [userRole, currentUser]);

  // Debounce search to prevent too many queries (250ms for snappier feel)
  useEffect(() => {
    const timer = setTimeout(() => {
      memoizedPerformSearch(searchQuery);
    }, 250);

    return () => clearTimeout(timer);
  }, [searchQuery, memoizedPerformSearch]);

  // Re-run search when category filters change
  useEffect(() => {
    if (searchQuery && searchQuery.length >= 2) {
      memoizedPerformSearch(searchQuery);
    }
  }, [selectedCategories, searchQuery, memoizedPerformSearch]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Command/Ctrl + K to open search (focus handled by useEffect when modal opens)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearchModal(true);
      }
      // Escape to close search modal
      if (e.key === 'Escape') {
        setShowSearchModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-focus search input when modal opens so user can type immediately
  useEffect(() => {
    if (!showSearchModal) return;
    const timeoutId = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [showSearchModal]);

  // Handle search input change
  const handleSearchInputChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleSearchResultSelect = useCallback(
    (item) => {
      if (!item) return;
      if (item.category === 'recent') {
        const q = item.query || item.title || '';
        setSearchQuery(q);
        return;
      }
      if (searchQuery.trim().length >= 2) {
        addRecentSearch(searchQuery.trim());
      }
      setShowSearchModal(false);
      if (item.category === 'properties') {
        navigate(`/properties/${item.id}`);
      } else if (item.category === 'tenants') {
        navigate(`/tenants/${item.id}`);
      } else if (item.category === 'maintenance') {
        navigate(`/maintenance/${item.id}`);
      } else if (item.category === 'finance') {
        navigate('/finance');
      } else if (item.category === 'payments') {
        navigate(`/edit-payment/${item.id}`);
      } else if (item.category === 'landlords') {
        navigate(`/landlords/${item.id}`);
      } else if (item.category === 'users') {
        navigate(`/settings?tab=users&userId=${item.id}`);
      } else if (item.category === 'links') {
        navigate('/links');
      } else if (item.category === 'documents') {
        navigate(item.propertyId ? `/properties/${item.propertyId}?tab=documents` : '/properties');
      } else if (item.category === 'certificates') {
        navigate('/compliance');
      } else if (item.category === 'calendar') {
        navigate('/calendar');
      } else if (item.category === 'messages') {
        navigate(`/messages?conversation=${item.id}`);
      } else if (item.category === 'pages' && item.path) {
        navigate(item.path);
      }
    },
    [navigate, searchQuery]
  );

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileNavOpen]);

  const resolvedBg = getTopbarBg(isDarkMode);
  const resolvedText = getTopbarTextColor(isDarkMode);
  const resolvedMuted = getTopbarMutedTextColor(isDarkMode);
  const controlColor = getTopbarControlColor(isDarkMode, primaryColor);

  const navPillActive = {
    background: primaryColor,
    color: primaryContrastText,
    boxShadow: `0 1px 4px ${primaryColor}55`,
  };

  const navPillInactive = {
    background: 'transparent',
    color: resolvedMuted,
  };

  const renderNavLink = (item, variant = 'desktop') => {
    const active = isNavItemActive(item.path, location.pathname);
    const label = item.shortTitle || item.title;

    return (
      <PageHandler
        key={item.path}
        path={item.path}
        className={`topbar-v2__nav-link topbar-v2__nav-link--${variant}${active ? ' topbar-v2__nav-link--active' : ''}`}
        aria-current={active ? 'page' : undefined}
        style={active ? navPillActive : navPillInactive}
      >
        {label}
      </PageHandler>
    );
  };

  const renderNavGroup = (group) => {
    if (group.type === 'link') {
      return renderNavLink(group.item, 'desktop');
    }

    const isAnyChildActive = group.children.some((child) =>
      isNavItemActive(child.path, location.pathname)
    );

    const dropdownBg = isDarkMode
      ? 'rgba(18, 22, 32, 0.98)'
      : 'rgba(255, 255, 255, 0.98)';
    const dropdownBorder = isDarkMode
      ? '1px solid rgba(255,255,255,0.08)'
      : '1px solid rgba(15,23,42,0.08)';
    const dropdownShadow = isDarkMode
      ? '0 8px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.25)'
      : '0 8px 32px rgba(15,23,42,0.1), 0 2px 8px rgba(15,23,42,0.06)';

    return (
      <Menu as="div" key={group.title} className="topbar-v2__nav-group">
        {({ open }) => (
          <>
            <Menu.Button
              className={`topbar-v2__nav-link topbar-v2__nav-link--desktop${isAnyChildActive ? ' topbar-v2__nav-link--active' : ''}`}
              style={isAnyChildActive ? navPillActive : navPillInactive}
            >
              {group.title}
              <FaChevronDown
                size={9}
                style={{
                  marginLeft: 5,
                  flexShrink: 0,
                  transition: 'transform 0.2s ease',
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  opacity: isAnyChildActive ? 0.7 : 0.45,
                }}
              />
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-150"
              enterFrom="opacity-0 scale-95 translate-y-[-4px]"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Menu.Items
                className="topbar-v2__nav-dropdown"
                style={{
                  background: dropdownBg,
                  border: dropdownBorder,
                  boxShadow: dropdownShadow,
                }}
              >
                {group.children.map((child) => {
                  const isActive = isNavItemActive(child.path, location.pathname);
                  return (
                    <Menu.Item key={child.path}>
                      {({ active: focused }) => (
                        <button
                          type="button"
                          className="topbar-v2__dropdown-item"
                          onClick={() => navigate(child.path)}
                          style={{
                            color: isActive ? primaryColor : resolvedText,
                            background: focused
                              ? (isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.05)')
                              : 'transparent',
                            fontWeight: isActive ? '600' : '400',
                          }}
                        >
                          {child.title}
                        </button>
                      )}
                    </Menu.Item>
                  );
                })}
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    );
  };

  return (
    <div
      className={`topbar topbar-v2 ${isDarkMode ? 'topbar-v2--dark' : 'topbar-v2--light'}`}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backgroundColor: resolvedBg,
        borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: isDarkMode
          ? '0 1px 0 rgba(255, 255, 255, 0.04), 0 8px 24px -12px rgba(0, 0, 0, 0.45)'
          : '0 1px 0 rgba(255, 255, 255, 0.8), 0 8px 24px -12px rgba(15, 23, 42, 0.08)',
        color: resolvedText,
        '--topbar-bg': resolvedBg,
        '--topbar-v2-primary': primaryColor,
        '--topbar-v2-text': resolvedText,
        '--topbar-v2-muted': resolvedMuted,
      }}
    >
      <div className="topbar-v2__primary">
        <div className="topbar-v2__brand">
          <button
            type="button"
            className="topbar-v2__logo-btn"
            onClick={() => navigate('/dashboard')}
            aria-label="Go to dashboard"
          >
            {displayLogo ? (
              <img src={displayLogo} alt="Company logo" className="topbar-v2__logo" />
            ) : (
              <img src="/favicon.jpg" alt="Plutoo" className="topbar-v2__logo" />
            )}
          </button>
        </div>

        <nav className="topbar-v2__nav topbar-v2__nav--desktop" aria-label="Primary navigation">
          <div
            className="topbar-v2__nav-scroll"
            style={{
              background: isDarkMode
                ? 'rgba(255,255,255,0.07)'
                : 'rgba(15,23,42,0.055)',
            }}
          >
            {filteredNavGroups.map((group) => renderNavGroup(group))}
          </div>
        </nav>

        <div className="topbar-v2__actions">
          {/* Mobile: individual icon buttons */}
          {(isMobile || isTablet) && (
            <>
              <button
                type="button"
                className="topbar-v2__icon-btn"
                onClick={() => setShowSearchModal(true)}
                ref={searchButtonRef}
                style={{ color: controlColor }}
                aria-label="Open search"
              >
                <FaSearch size={16} />
              </button>
              {currentUser && checkPageAccess('notifications') && (
                <button
                  type="button"
                  className="topbar-v2__icon-btn"
                  onClick={handleNotificationsClick}
                  style={{ color: controlColor, position: 'relative' }}
                  aria-label={notificationCount > 0 ? `Notifications, ${notificationCount} unread` : 'Notifications'}
                >
                  <FaBell size={16} aria-hidden />
                  {notificationCount > 0 && (
                    <span className="notification-count">{notificationCount > 99 ? '99+' : notificationCount}</span>
                  )}
                </button>
              )}
              {userRole && userRole.toLowerCase() === 'administrator' && (
                <button
                  type="button"
                  className="topbar-v2__icon-btn"
                  onClick={toggleTheme}
                  style={{ color: controlColor }}
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? <FaMoon size={16} /> : <FaSun size={16} />}
                </button>
              )}
            </>
          )}

          {/* Desktop: combined utility pill */}
          {!isMobile && !isTablet && (
            <div
              className="topbar-v2__utility-pill"
              style={{
                background: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.055)',
              }}
            >
              <button
                type="button"
                className="topbar-v2__utility-item"
                onClick={() => setShowSearchModal(true)}
                ref={searchButtonRef}
                aria-label="Open search"
                style={{ color: resolvedText }}
              >
                <FaSearch size={14} />
              </button>

              {currentUser && checkPageAccess('notifications') && (
                <>
                  <span className="topbar-v2__utility-divider" aria-hidden />
                  <button
                    type="button"
                    className="topbar-v2__utility-item"
                    onClick={handleNotificationsClick}
                    style={{ color: controlColor, position: 'relative' }}
                    aria-label={notificationCount > 0 ? `Notifications, ${notificationCount} unread` : 'Notifications'}
                  >
                    <FaBell size={14} aria-hidden />
                    {notificationCount > 0 && (
                      <span className="notification-count">{notificationCount > 99 ? '99+' : notificationCount}</span>
                    )}
                  </button>
                </>
              )}

              {userRole && userRole.toLowerCase() === 'administrator' && (
                <>
                  <span className="topbar-v2__utility-divider" aria-hidden />
                  <button
                    type="button"
                    className="topbar-v2__utility-item"
                    onClick={toggleTheme}
                    style={{ color: resolvedText }}
                    aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  >
                    {isDarkMode ? <FaMoon size={14} /> : <FaSun size={14} />}
                  </button>
                </>
              )}
            </div>
          )}

          <Menu as="div" className="relative">
            <Menu.Button
              className="topbar-v2__avatar-pill"
              style={{
                background: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(15,23,42,0.055)',
              }}
            >
              {currentUser?.photoURL ? (
                <img
                  src={currentUser.photoURL}
                  alt="Profile"
                  className="topbar-v2__avatar-img"
                />
              ) : (
                <div
                  className="topbar-v2__avatar-fallback"
                  style={{ backgroundColor: primaryColor, color: primaryContrastText }}
                >
                  {getUserInitial()}
                </div>
              )}
              {!isMobile && !isTablet && (
                <div className="topbar-v2__avatar-info">
                  <span
                    className="topbar-v2__avatar-name"
                    style={{ color: resolvedText }}
                  >
                    {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Account'}
                  </span>
                  {currentUser?.email && (
                    <span
                      className="topbar-v2__avatar-email"
                      style={{ color: resolvedMuted }}
                    >
                      {currentUser.email}
                    </span>
                  )}
                </div>
              )}
              {!isMobile && !isTablet && (
                <FaChevronDown
                  size={10}
                  style={{ color: resolvedMuted, flexShrink: 0 }}
                />
              )}
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <AvatarUserMenu
                currentUser={currentUser}
                userRole={userRole}
                isDarkMode={isDarkMode}
                primaryColor={primaryColor}
                primaryContrastText={primaryContrastText}
                getUserInitial={getUserInitial}
                logoutLoading={logoutLoading}
                onNavigate={navigate}
                onLogout={handleLogout}
              />
            </Transition>
          </Menu>

          {(isMobile || isTablet) && (
            <button
              type="button"
              className="topbar-v2__icon-btn topbar-v2__menu-btn"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation menu"
            >
              <FaBars size={16} />
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {mobileNavOpen && (
          <>
            <motion.button
              type="button"
              className="topbar-v2__mobile-backdrop"
              aria-label="Close navigation menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileNavOpen(false)}
            />
            <motion.nav
              className="topbar-v2__mobile-nav"
              aria-label="Mobile navigation"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="topbar-v2__mobile-nav-header">
                <span className="topbar-v2__mobile-nav-title">Navigation</span>
                <button
                  type="button"
                  className="topbar-v2__icon-btn"
                  onClick={() => setMobileNavOpen(false)}
                  aria-label="Close navigation menu"
                >
                  <FaTimes size={16} />
                </button>
              </div>
              <div className="topbar-v2__mobile-nav-links">
                {filteredNavItems.map((item) => renderNavLink(item, 'mobile'))}
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>

      <GlobalSearchModal
        open={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        modalRef={modalRef}
        isDarkMode={isDarkMode}
        primaryColor={primaryColor}
        searchQuery={searchQuery}
        onSearchInputChange={handleSearchInputChange}
        searchInputRef={searchInputRef}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters((prev) => !prev)}
        selectedCategories={selectedCategories}
        onToggleCategory={toggleCategory}
        onClearCategories={() => setSelectedCategories([])}
        availableCategories={getAvailableCategories()}
        searchResults={searchResults}
        isSearching={isSearching}
        onSelectResult={handleSearchResultSelect}
      />

    </div>
  );
};

export default TopBarV2;
